(() => {
  if (customElements.get('gvs-card-rail-controls')) return;

  class GvsCardRailControls extends HTMLElement {
    connectedCallback() {
      this.track = document.getElementById(this.dataset.trackId);
      this.previousButton = this.querySelector('[data-gvs-rail-previous]');
      this.nextButton = this.querySelector('[data-gvs-rail-next]');
      this.currentOutput = this.querySelector('[data-gvs-rail-current]');
      this.items = this.track
        ? Array.from(this.track.querySelectorAll('[data-gvs-rail-item]'))
        : [];
      this.activated = false;

      if (!this.track || !this.previousButton || !this.nextButton || this.items.length < 2) {
        this.hidden = true;
        return;
      }

      this.handlePrevious = () => this.move(-1);
      this.handleNext = () => this.move(1);
      this.handleScroll = () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.update(), 80);
      };

      // Rails are below the fold on current GVS journeys. Avoid initial geometry
      // reads/ResizeObservers until the section is close enough to interact with.
      if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
        this.activationObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.activationObserver?.disconnect();
            this.activationObserver = null;
            this.activate();
          },
          { rootMargin: '500px 0px' }
        );
        this.activationObserver.observe(this.track);
      } else {
        this.activate();
      }
    }

    activate() {
      if (this.activated) return;
      this.activated = true;

      this.previousButton.addEventListener('click', this.handlePrevious);
      this.nextButton.addEventListener('click', this.handleNext);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
      }

      this.update();
    }

    disconnectedCallback() {
      window.clearTimeout(this.scrollTimer);
      this.activationObserver?.disconnect();
      this.activationObserver = null;

      if (this.activated) {
        this.previousButton?.removeEventListener('click', this.handlePrevious);
        this.nextButton?.removeEventListener('click', this.handleNext);
        this.track?.removeEventListener('scroll', this.handleScroll);
        this.resizeObserver?.disconnect();
      }

      this.activated = false;
    }

    getCurrentIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const styles = window.getComputedStyle(this.track);
      const inset = Number.parseFloat(styles.scrollPaddingInlineStart || styles.paddingInlineStart) || 0;
      const targetLeft = trackRect.left + inset;

      const itemLefts = this.items.map((item) => item.getBoundingClientRect().left);
      let closestIndex = 0;
      let closestDistance = Math.abs(itemLefts[0] - targetLeft);

      for (let index = 1; index < itemLefts.length; index += 1) {
        const currentDistance = Math.abs(itemLefts[index] - targetLeft);
        if (currentDistance < closestDistance) {
          closestIndex = index;
          closestDistance = currentDistance;
        }
      }

      return closestIndex;
    }

    move(direction) {
      const currentIndex = this.getCurrentIndex();
      const nextIndex = Math.max(0, Math.min(this.items.length - 1, currentIndex + direction));
      const item = this.items[nextIndex];
      const trackRect = this.track.getBoundingClientRect();
      const styles = window.getComputedStyle(this.track);
      const inset = Number.parseFloat(styles.scrollPaddingInlineStart || styles.paddingInlineStart) || 0;
      const itemLeft = item.getBoundingClientRect().left;
      const left = this.track.scrollLeft + itemLeft - trackRect.left - inset;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.update(nextIndex);
    }

    update(forcedIndex) {
      const currentIndex = Number.isInteger(forcedIndex) ? forcedIndex : this.getCurrentIndex();

      this.previousButton.disabled = currentIndex === 0;
      this.nextButton.disabled = currentIndex === this.items.length - 1;

      if (this.currentOutput) {
        this.currentOutput.textContent = String(currentIndex + 1);
      }
    }
  }

  customElements.define('gvs-card-rail-controls', GvsCardRailControls);
})();